package com.jingfangpai.syndrome.model

import com.jingfangpai.common.orm.mapping.SyndromeIndication
import com.jingfangpai.common.orm.mapping.enums.MedicalRecordSignSymptomState
import com.jingfangpai.common.orm.mapping.enums.SignSymptomName
import com.jingfangpai.common.orm.mapping.enums.SyndromeIndicationSignSymptomRule
import com.jingfangpai.common.orm.mapping.enums.SyndromeIndicationSignSymptomType
import com.jingfangpai.medicalRecord.model.MedicalRecordDiagnosisRejectedBy
import com.jingfangpai.medicalRecord.model.MedicalRecordDiagnosisSuggestionDto
import com.jingfangpai.medicalRecord.model.MedicalRecordDiagnosisVotedBy
import com.jingfangpai.medicalRecord.model.MedicalRecordSignSymptomDto
import kotlinx.serialization.Serializable

@Serializable
class SyndromeIndicationDetail:SyndromeIndication() {
    var syndromeName:String? = null
    var signSymptomBaseIndicationDetail:SyndromeIndicationDetail?=null
    var signSymptomBaseIndicationName:String?=null
    var symptoms:Set<SyndromeIndicationSignSymptomDetail> = setOf()

    /**
     * 获取相关的症状名称，组合的要展开为单项，基础适应证的也要包含进来
     */
    fun getConfiguredSignSymptomNames():Set<SignSymptomName>{
        val signSymptomNames = mutableSetOf<SignSymptomName>()
        for(syndromeIndicationSignSymptomDetail in symptoms){
            if(SyndromeIndicationSignSymptomType.单项 == syndromeIndicationSignSymptomDetail.type && syndromeIndicationSignSymptomDetail.signSymptomName != null){
                signSymptomNames += syndromeIndicationSignSymptomDetail.signSymptomName!!
            } else if(SyndromeIndicationSignSymptomType.组合 == syndromeIndicationSignSymptomDetail.type && !syndromeIndicationSignSymptomDetail.children.isNullOrEmpty()){
                for(child in syndromeIndicationSignSymptomDetail.children){
                    if(SyndromeIndicationSignSymptomType.单项 == child.type && child.signSymptomName != null){
                        signSymptomNames += child.signSymptomName!!
                    }
                }
            }
        }
        if(signSymptomBaseIndicationDetail != null){
            signSymptomNames += signSymptomBaseIndicationDetail!!.getConfiguredSignSymptomNames()
        }
        return signSymptomNames;
    }

    /**
     * 生成诊断建议
     */
    fun generateSuggestion(inputSymptoms:Set<MedicalRecordSignSymptomDto>): MedicalRecordDiagnosisSuggestionDto {
        val suggestionDto = MedicalRecordDiagnosisSuggestionDto()
        suggestionDto.syndromeId = this.syndromeId
        suggestionDto.syndromeName = this.syndromeName
        suggestionDto.syndromeIndicationId = this.id
        suggestionDto.syndromeIndicationName = this.name
        suggestionDto.syndromeIndicationDescription = this.description
        var baseSuggestion:MedicalRecordDiagnosisSuggestionDto? = null
        if(!signSymptomBaseIndicationId.isNullOrBlank()){
            if(signSymptomBaseIndicationDetail != null){
                baseSuggestion = signSymptomBaseIndicationDetail!!.generateSuggestion(inputSymptoms)
            }else{
                throw Exception("存在基础适应证但其值未正确获取")
            }
        }

        if(baseSuggestion == null || (baseSuggestion.score != null && baseSuggestion.score!! >= 60)){ //基础满足才进行下一步
            //所有存在的症状
            val allExistingSymptoms = inputSymptoms.filter { MedicalRecordSignSymptomState.存在 == it.state && it.signSymptomName !=null}.map { it.signSymptomName!! }.toTypedArray()
            val allOptionalSymptoms = this.symptoms.filter { SyndromeIndicationSignSymptomRule.可见 == it.rule }
            val allFindOneMeansAllSymptoms = this.symptoms.filter { SyndromeIndicationSignSymptomRule.但见即是 == it.rule }
            //所有必见症状
            val mustBeFound = this.symptoms.filter { SyndromeIndicationSignSymptomRule.必见 == it.rule }

            var findOneRejectAllButFound = arrayOf<SyndromeIndicationSignSymptomDetail>()
            var mustBeFoundButActualNotFound = arrayOf<SyndromeIndicationSignSymptomDetail>()
            var findOneMeansAllAndFound = arrayOf<SyndromeIndicationSignSymptomDetail>()
            var mustBeFoundAndFound = arrayOf<SyndromeIndicationSignSymptomDetail>()
            var optionalAndFound = arrayOf<SyndromeIndicationSignSymptomDetail>()
            for(syndromeIndicationSignSymptomDetail in this.symptoms){
                val matchResult = syndromeIndicationSignSymptomDetail.match(inputSymptoms.toTypedArray())
                if(matchResult.isMatched && SyndromeIndicationSignSymptomRule.但见即非 == syndromeIndicationSignSymptomDetail.rule){ //但见即非
                    findOneRejectAllButFound += syndromeIndicationSignSymptomDetail
                }
                if(!matchResult.isMatched && SyndromeIndicationSignSymptomRule.必见 == syndromeIndicationSignSymptomDetail.rule){
                    mustBeFoundButActualNotFound += syndromeIndicationSignSymptomDetail
                }
                if(matchResult.isMatched && SyndromeIndicationSignSymptomRule.但见即是 == syndromeIndicationSignSymptomDetail.rule){
                    findOneMeansAllAndFound += syndromeIndicationSignSymptomDetail
                }
                if(matchResult.isMatched && SyndromeIndicationSignSymptomRule.必见 == syndromeIndicationSignSymptomDetail.rule){
                    mustBeFoundAndFound += syndromeIndicationSignSymptomDetail
                }
                if(matchResult.isMatched && SyndromeIndicationSignSymptomRule.可见 == syndromeIndicationSignSymptomDetail.rule){
                    optionalAndFound += syndromeIndicationSignSymptomDetail
                }
            }
            //但见即非
            if(findOneRejectAllButFound.size>0){
                suggestionDto.score = 0
                suggestionDto.rejectedBy = MedicalRecordDiagnosisRejectedBy(findOneRejectAllButFound,arrayOf())
                suggestionDto.description = "满足以下症状（但见即非）：<em>${findOneRejectAllButFound.map { it.getDisplayName(allExistingSymptoms) }.joinToString("、")}</em>"
            }else{
                //必见症状是否均见
                if(mustBeFoundButActualNotFound.size>0){
                    suggestionDto.score = 0
                    suggestionDto.rejectedBy = MedicalRecordDiagnosisRejectedBy(arrayOf(),mustBeFoundButActualNotFound)
                    suggestionDto.description = "必见症状未满足：<em>${mustBeFoundButActualNotFound.map { it.getDisplayName(allExistingSymptoms) }.joinToString("、")}</em>"
                }else{
                    //但见即是
                    if(findOneMeansAllAndFound.isNotEmpty()){
                        suggestionDto.score = 100
                        suggestionDto.votedBy = MedicalRecordDiagnosisVotedBy(findOneMeansAllAndFound,mustBeFoundAndFound,optionalAndFound)
                        suggestionDto.description = "满足以下症状（但见即是）：<em>${findOneMeansAllAndFound.map { it.getDisplayName(allExistingSymptoms) }.joinToString("、")}</em>"
                    }else{
                        //必见和可见
                        val allOptionalSymptom = this.symptoms.filter { SyndromeIndicationSignSymptomRule.可见 == it.rule}
                        val allOptionalSymptomTotalScore = allOptionalSymptom.sumOf { it.score?:0 }*1.0
                        var optionalScoreSum = 0.0;
                        optionalAndFound.forEach { optionalSymptom ->
                            optionalScoreSum += (optionalSymptom.score ?: 0) * 1.0
                        }
                        val finalOptionScore = (optionalScoreSum/(if(allOptionalSymptomTotalScore<1.0) 1.0 else allOptionalSymptomTotalScore))*30.0
                        val finalMustFoundScore = (mustBeFoundAndFound.size/(if(mustBeFound.size<1) 1 else mustBeFound.size))*(if(allOptionalSymptomTotalScore<1) (90.0+mustBeFoundAndFound.size) else 70.0)
                        suggestionDto.score = finalMustFoundScore.toInt() + finalOptionScore.toInt()
                        suggestionDto.votedBy = MedicalRecordDiagnosisVotedBy(findOneMeansAllAndFound,mustBeFoundAndFound,optionalAndFound)
                        suggestionDto.description =  if(mustBeFoundAndFound.size>0) "满足必见症状<em>${mustBeFoundAndFound.map{it.getDisplayName(allExistingSymptoms)}.joinToString("、")}</em>，满足以下可见症状：<em>${optionalAndFound.map{it.getDisplayName(allExistingSymptoms)}.joinToString("、")}</em>" else "满足以下可见症状：<em>${optionalAndFound.map{it.getDisplayName(allExistingSymptoms)}.joinToString("、")}</em>"
                    }
                }
            }
            suggestionDto.ignored = (allFindOneMeansAllSymptoms.filter { !findOneMeansAllAndFound.contains(it) } + allOptionalSymptoms.filter { !optionalAndFound.contains(it) }).toTypedArray()
        }else{
            suggestionDto.score = 0
            suggestionDto.rejectedBy = baseSuggestion.rejectedBy
            suggestionDto.description = "基础适应证未满足：${baseSuggestion.description}"
        }

        return suggestionDto
    }


}